import { useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Box, Typography, Stack, Paper, CircularProgress, Chip, Button, Tooltip, Divider, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Alert,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import CloudQueueIcon from '@mui/icons-material/CloudQueue'
import { getVersionMap, refreshVersionMap } from '../../api/client'
import type { VersionMapEntry } from '../../api/types'
import NetworkTopologyPanel from './NetworkTopologyPanel'

const REGION_ABBR: Record<string, string> = {
  SouthCentralUS: 'SCUS',
  NorthCentralUS: 'NCUS',
  CentralUS: 'CUS',
  EastUS: 'EUS',
  EastUS2: 'EUS2',
  'SouthCentralUS-Primary': 'SCUS*',
  Primary: '',
}

function regionLabel(region: string) {
  return REGION_ABBR[region] ?? region
}

function relativeTime(iso: string) {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  return `${Math.floor(hrs / 24)}d ago`
}

function SummaryChip({ label, count, color }: { label: string; count: number; color: string }) {
  return (
    <Chip label={`${count} ${label}`} size="small"
      sx={{ bgcolor: color + '18', color, fontWeight: 700, border: `1px solid ${color}33` }} />
  )
}

// ── Version chip ────────────────────────────────────────────────────────────

function OpenAppServiceLink({ url }: { url: string }) {
  return (
    <Tooltip title={`Open ${url}`}>
      <IconButton size="small" component="a" href={url} target="_blank" rel="noreferrer"
        sx={{ p: 0.25 }}>
        <OpenInNewIcon sx={{ fontSize: 13 }} />
      </IconButton>
    </Tooltip>
  )
}

function azurePortalUrl(resourceId: string): string {
  return `https://portal.azure.com/#resource${resourceId}/appServices`
}

function OpenAzurePortalLink({ resourceId }: { resourceId: string }) {
  return (
    <Tooltip title="Open in Azure Portal">
      <IconButton size="small" component="a" href={azurePortalUrl(resourceId)} target="_blank" rel="noreferrer"
        sx={{ p: 0.25 }}>
        <CloudQueueIcon sx={{ fontSize: 13 }} />
      </IconButton>
    </Tooltip>
  )
}

function VersionChip({ entry }: { entry?: VersionMapEntry }) {
  if (!entry) return <Typography variant="caption" color="text.secondary">—</Typography>

  if (entry.status !== 'Ok') {
    const fallbackTooltip = entry.status === 'Unreachable'
      ? 'Could not reach this App Service'
      : 'No version string found on the homepage'

    return (
      <Stack direction="row" alignItems="center" spacing={0.25}>
        <Tooltip title={entry.unreachableReason ?? fallbackTooltip}>
          <Chip label={entry.unreachableReason ? `not detected — ${entry.unreachableReason}` : 'not detected'} size="small"
            sx={{ bgcolor: '#64748b18', color: '#64748b', fontWeight: 600, fontSize: 11, height: 20 }} />
        </Tooltip>
        <OpenAppServiceLink url={entry.url} />
        {entry.azureResourceId && <OpenAzurePortalLink resourceId={entry.azureResourceId} />}
      </Stack>
    )
  }

  const color = entry.flaggedProdRegionMismatch ? '#dc2626' : entry.flaggedBehindProd ? '#d97706' : '#16a34a'
  const shortSha = entry.commitSha?.slice(0, 7) ?? 'unknown'

  const tooltip = [
    entry.isInferred ? 'Estimated from Azure DevOps deployment history — the live version page requires sign-in' : null,
    `Full SHA: ${entry.commitSha ?? 'unknown'}`,
    entry.buildTimestamp ? `built ${new Date(entry.buildTimestamp).toLocaleDateString()}` : null,
    entry.flaggedProdRegionMismatch ? "Prod's regions disagree on this commit" : null,
    entry.flaggedBehindProd ? 'behind the commit already running in Prod' : null,
  ].filter(Boolean).join(' · ')

  return (
    <Stack spacing={0.25}>
      <Stack direction="row" alignItems="center" spacing={0.25}>
        <Tooltip title={tooltip}>
          <Chip label={`${entry.isInferred ? '≈ ' : ''}v${entry.version} · ${shortSha}`} size="small"
            sx={{ bgcolor: color + '18', color, fontWeight: 600, fontSize: 11, height: 20, fontFamily: 'monospace' }} />
        </Tooltip>
        <OpenAppServiceLink url={entry.url} />
        {entry.azureResourceId && <OpenAzurePortalLink resourceId={entry.azureResourceId} />}
      </Stack>
      {entry.commitsBehindDevelop != null && (
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.5, lineHeight: 1.2 }}>
          {entry.commitsBehindDevelop === 0 ? 'up to date with develop' : `${entry.commitsBehindDevelop} behind develop`}
        </Typography>
      )}
      {entry.releaseBranch && (
        <Typography variant="caption" sx={{ fontSize: 10.5, lineHeight: 1.2 }}>
          <a href={entry.releaseBranchUrl ?? undefined} target="_blank" rel="noreferrer"
            style={{ color: 'inherit', opacity: 0.75 }}>
            {entry.releaseBranch}
          </a>
        </Typography>
      )}
    </Stack>
  )
}

function ProdCell({ entries }: { entries: VersionMapEntry[] }) {
  if (entries.length === 0) return <Typography variant="caption" color="text.secondary">—</Typography>
  return (
    <Stack spacing={1}>
      {entries.map(e => (
        <Stack key={e.region} direction="row" spacing={0.75} alignItems="flex-start">
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 34, fontSize: 10.5, pt: 0.4 }}>
            {regionLabel(e.region)}
          </Typography>
          <VersionChip entry={e} />
        </Stack>
      ))}
    </Stack>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function VersionMapPage() {
  const qc = useQueryClient()

  const { data: resp, isLoading, error } = useQuery({
    queryKey: ['version-map'],
    queryFn: getVersionMap,
    staleTime: 60_000,
  })

  const refreshMut = useMutation({
    mutationFn: refreshVersionMap,
    onSuccess: (data) => {
      if (data.data) qc.setQueryData(['version-map'], data)
    },
  })

  const report = resp?.data

  const rows = useMemo(() => {
    if (!report) return []
    const byService = new Map<string, VersionMapEntry[]>()
    for (const e of report.entries) {
      const list = byService.get(e.serviceName) ?? []
      list.push(e)
      byService.set(e.serviceName, list)
    }
    return [...byService.entries()]
      .map(([serviceName, entries]) => ({
        serviceName,
        acc: entries.find(e => e.environment === 'ACC'),
        uat: entries.find(e => e.environment === 'UAT'),
        prod: entries.filter(e => e.environment === 'Prod'),
      }))
      .sort((a, b) => a.serviceName.localeCompare(b.serviceName))
  }, [report])

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h6" fontWeight={700}>Environments</Typography>
          <Typography variant="caption" color="text.secondary">
            Deployed version per service, read live from each App Service's homepage
            {report && ` · refreshed ${relativeTime(report.generatedAtUtc)}`}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          {report && (
            <>
              <SummaryChip label="tracked" count={report.trackedCount} color="#2563eb" />
              {report.unreachableCount > 0 &&
                <SummaryChip label="unreachable" count={report.unreachableCount} color="#64748b" />}
              {report.behindProdCount > 0 &&
                <SummaryChip label="behind Prod" count={report.behindProdCount} color="#d97706" />}
              {report.prodRegionMismatchCount > 0 &&
                <SummaryChip label="Prod region mismatch" count={report.prodRegionMismatchCount} color="#dc2626" />}
            </>
          )}
          <Button variant="outlined" size="small" startIcon={<RefreshIcon />}
            onClick={() => refreshMut.mutate()}
            disabled={refreshMut.isPending || isLoading}>
            {refreshMut.isPending ? 'Refreshing…' : 'Refresh'}
          </Button>
        </Stack>
      </Stack>

      {/* Legend */}
      <Stack direction="row" spacing={3} mb={2} flexWrap="wrap">
        {[
          { color: '#16a34a', label: 'Matches expectations' },
          { color: '#d97706', label: 'Behind the commit already in Prod' },
          { color: '#dc2626', label: "Prod's own regions disagree" },
          { color: '#64748b', label: 'Unreachable / no version detected' },
        ].map(({ color, label }) => (
          <Stack key={label} direction="row" alignItems="center" spacing={0.75}>
            <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: color }} />
            <Typography variant="caption" color="text.secondary">{label}</Typography>
          </Stack>
        ))}
      </Stack>

      {isLoading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}

      {error && <Alert severity="error">Failed to load the version map.</Alert>}

      {report && (
        <Paper variant="outlined">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Service</TableCell>
                  <TableCell>ACC</TableCell>
                  <TableCell>UAT</TableCell>
                  <TableCell>
                    <Tooltip title="Prod runs a South Central US / North Central US pair for most services, plus an East US satellite for a few — each region is shown separately so drift between them is visible.">
                      <span style={{ cursor: 'help', borderBottom: '1px dotted currentColor' }}>Prod</span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(row => (
                  <TableRow key={row.serviceName} hover>
                    <TableCell sx={{ fontWeight: 600, fontSize: 13.5 }}>{row.serviceName}</TableCell>
                    <TableCell><VersionChip entry={row.acc} /></TableCell>
                    <TableCell><VersionChip entry={row.uat} /></TableCell>
                    <TableCell><ProdCell entries={row.prod} /></TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No targets configured.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Typography variant="caption" color="text.secondary" display="block" mt={2}>
        Covers services hosted on Azure App Service only. On-prem/IIS-hosted fabrics (Hyper-V, vSphere/ESX,
        FileShare, DataCenter, LOD-SES, LOD-CodeHost, Docker LabHostService) don't expose the same version
        footer and aren't tracked here yet.
      </Typography>

      <Divider sx={{ my: 4 }} />

      <NetworkTopologyPanel />
    </Box>
  )
}
